import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';

export interface RevenueProfitDataPoint {
  week: string;
  revenue: number;
  grossProfit: number;
}

export interface RevenueProfitData {
  revenueProfitData: RevenueProfitDataPoint[];
}

export const useFourWeekRevenueProfitData = (companyName?: string, productId?: string) => {
  const [data, setData] = useState<RevenueProfitData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!companyName || !productId) {
        setData(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const docRef = doc(db, 'pmt2_data', companyName, 'products', productId);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
          setData(null);
          setIsLoading(false);
          return;
        }

        const docData = docSnap.data();
        
        // Parse pipe-separated data
        const parseData = (value: any): number[] => {
          if (!value || typeof value !== 'string') return [0, 0, 0, 0];
          
          try {
            return value.split('|').map((v: string) => {
              const num = parseFloat(v.trim());
              return isNaN(num) ? 0 : num;
            });
          } catch (error) {
            console.warn('Error parsing data:', error);
            return [0, 0, 0, 0];
          }
        };

        const revenueData = parseData(docData['4 Week Overall Trend: Revenue']);
        const grossProfitData = parseData(docData['4 Week Overall Trend: Gross Profit']);

        // Create chart data points
        const revenueProfitData: RevenueProfitDataPoint[] = [
          { week: 'Week 1', revenue: revenueData[0], grossProfit: grossProfitData[0] },
          { week: 'Week 2', revenue: revenueData[1], grossProfit: grossProfitData[1] },
          { week: 'Week 3', revenue: revenueData[2], grossProfit: grossProfitData[2] },
          { week: 'Week 4', revenue: revenueData[3], grossProfit: grossProfitData[3] },
        ];

        setData({ revenueProfitData });
      } catch (err) {
        console.error('Error fetching 4-week revenue/profit data:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch data'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [companyName, productId]);

  return { data, isLoading, error };
};